// ═══════════════════════════════════════════════════════════════════════════
// AdminUsers — onglet « Utilisateurs » du tableau de bord : liste complète,
// 100 par page chargés au fil du scroll, tri par colonne, filtres, recherche
// login / e-mail. Données : GET /api/admin/users (en-tête x-admin-key).
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API_BASE = import.meta.env.VITE_API_BASE || "/api";
const PAGE = 100;

const fmt = (n) => (n == null ? "–" : new Intl.NumberFormat("fr-FR").format(n));
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "–");
const dateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "–");
const ago = (iso) => {
  if (!iso) return "jamais";
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return d < 60 ? `il y a ${d} j` : `il y a ${Math.floor(d / 30)} mois`;
};
const PROVIDER_LABEL = { APPLE: "Apple", GOOGLE: "Google", PASSWORD: "Mdp" };
const DEVICE_LABEL = { ios: "iPhone", android: "Android" };

// Origine de l'inscription : donnée sûre (posée par le serveur au moment où
// un compte récent consomme un lien), sinon heuristiques sur l'historique.
function origin(u) {
  const ref = u.acquisitionRefLogin || "?";
  if (u.acquisitionType === "friend_link") return { label: `Lien ami de ${ref}`, sure: true };
  if (u.acquisitionType === "crew_link") return { label: `Lien crew de ${ref}`, sure: true };
  if (u.acquisitionType === "dispo_link") return { label: `Lien dispo de ${ref}`, sure: true };
  if (u.guestBefore) return { label: "Invité web (a répondu à une dispo avant de s'inscrire)", sure: false };
  const fc = u.firstContact;
  if (fc?.at) {
    const days = (new Date(fc.at) - new Date(u.createdAt)) / 86_400_000;
    if (days <= 7) {
      return fc.direction === "received"
        ? { label: `Probablement via ${fc.login} (demande d'ami reçue ${days < 1 ? "le jour même" : `après ${Math.round(days)} j`})`, sure: false }
        : { label: `A ajouté ${fc.login} en premier`, sure: false };
    }
  }
  if (u.firstHost?.login) return { label: `Probablement via une dispo de ${u.firstHost.login}`, sure: false };
  return { label: "Inconnue (organique ?)", sure: false };
}

const Select = ({ label, value, onChange, options }) => (
  <label className="flex flex-col gap-1 text-xs text-slate-400">
    {label}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg bg-slate-950 border border-slate-700 px-2 py-1.5 text-sm text-slate-200 outline-none focus:border-sky-400"
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  </label>
);

const COLUMNS = [
  { key: "login", label: "Login", sort: "login" },
  { key: "email", label: "E-mail", sort: "email" },
  { key: "createdAt", label: "Inscrit le", sort: "createdAt" },
  { key: "lastActiveAt", label: "Dernière activité", sort: "lastActiveAt" },
  { key: "providers", label: "Via" },
  { key: "devices", label: "Appareil" },
  { key: "locale", label: "Langue" },
  { key: "friends", label: "Amis", sort: "friends", num: true },
  { key: "crews", label: "Crews", sort: "crews", num: true },
  { key: "messages", label: "Messages", sort: "messages", num: true },
  { key: "dispos", label: "Dispos", sort: "dispos", num: true },
  { key: "participations", label: "Particip.", sort: "participations", num: true },
  { key: "pushTokens", label: "Notifs", num: true },
  { key: "hasAvatar", label: "Photo" },
  { key: "origin", label: "Origine" },
];

const DEFAULT_FILTERS = { q: "", provider: "", device: "", locale: "", activity: "", signup: "", avatar: "", push: "", origin: "" };

export default function AdminUsers({ apiKey, onUnauthorized }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const sentinel = useRef(null);
  const reqId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (debouncedQ) p.set("q", debouncedQ);
    for (const k of ["provider", "device", "locale", "activity", "signup", "avatar", "push", "origin"]) {
      if (filters[k]) p.set(k, filters[k]);
    }
    p.set("sort", sort.key);
    p.set("dir", sort.dir);
    p.set("limit", String(PAGE));
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, filters.provider, filters.device, filters.locale, filters.activity, filters.signup, filters.avatar, filters.push, filters.origin, sort]);

  const load = useCallback(
    async (offset) => {
      const id = ++reqId.current;
      setLoading(true);
      setError("");
      try {
        const p = new URLSearchParams(query);
        p.set("offset", String(offset));
        const res = await fetch(`${API_BASE}/admin/users?${p}`, { headers: { "x-admin-key": apiKey } });
        if (res.status === 401) return onUnauthorized?.();
        if (!res.ok) throw new Error(`Erreur serveur (${res.status})`);
        const json = await res.json();
        if (id !== reqId.current) return; // réponse obsolète (filtre changé entre-temps)
        setRows((prev) => (offset === 0 ? json.users : [...prev, ...json.users]));
        if (json.total != null) setTotal(json.total);
      } catch (e) {
        if (id === reqId.current) setError(e.message || "Erreur");
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    },
    [query, apiKey, onUnauthorized]
  );

  // Nouveau tri / filtre → on repart de zéro.
  useEffect(() => {
    setRows([]);
    setTotal(null);
    setExpanded(null);
    load(0);
  }, [load]);

  // Scroll infini : le sentinelle en bas de table déclenche la page suivante.
  const hasMore = total != null && rows.length < total;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) load(rows.length);
      },
      { rootMargin: "400px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, rows.length, load]);

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : s.key === key ? "desc" : key === "login" || key === "email" ? "asc" : "desc" }));
  const activeFilters = Object.entries(filters).filter(([, v]) => v).length;

  const cell = (u, c) => {
    switch (c.key) {
      case "login":
        return <span className="font-semibold text-white">{u.login}</span>;
      case "email":
        return <span className="text-slate-300">{u.email}</span>;
      case "createdAt":
        return <span title={dateTime(u.createdAt)}>{date(u.createdAt)}</span>;
      case "lastActiveAt":
        return <span title={dateTime(u.lastActiveAt)} className={u.lastActiveAt && Date.now() - new Date(u.lastActiveAt) < 7 * 86_400_000 ? "text-emerald-400" : "text-slate-400"}>{ago(u.lastActiveAt)}</span>;
      case "providers":
        return u.providers.map((p) => PROVIDER_LABEL[p] || p).join(", ") || "–";
      case "devices":
        return u.devices.map((d) => DEVICE_LABEL[d] || d).join(", ") || "–";
      case "locale":
        return u.locale;
      case "pushTokens":
        return u.pushTokens > 0 ? `oui (${u.pushTokens})` : <span className="text-slate-500">non</span>;
      case "hasAvatar":
        return u.hasAvatar ? "oui" : <span className="text-slate-500">non</span>;
      case "origin": {
        const o = origin(u);
        return <span className={o.sure ? "text-sky-300" : "text-slate-400"}>{o.label}</span>;
      }
      default:
        return c.num ? fmt(u[c.key]) : String(u[c.key] ?? "–");
    }
  };

  return (
    <div>
      {/* Filtres */}
      <section className="rounded-2xl bg-slate-900/70 border border-slate-800 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-1 text-xs text-slate-400 flex-1 min-w-[220px]">
          Recherche
          <input
            value={filters.q}
            onChange={(e) => setF("q")(e.target.value)}
            placeholder="login ou e-mail"
            className="rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-sm text-slate-200 outline-none focus:border-sky-400"
          />
        </label>
        <Select label="Connexion" value={filters.provider} onChange={setF("provider")} options={[["", "Toutes"], ["APPLE", "Apple"], ["GOOGLE", "Google"], ["PASSWORD", "Mot de passe"]]} />
        <Select label="Appareil" value={filters.device} onChange={setF("device")} options={[["", "Tous"], ["ios", "iPhone"], ["android", "Android"]]} />
        <Select label="Langue" value={filters.locale} onChange={setF("locale")} options={[["", "Toutes"], ["fr", "Français"], ["en", "Anglais"]]} />
        <Select label="Activité" value={filters.activity} onChange={setF("activity")} options={[["", "Toutes"], ["7", "Actif 7 j"], ["30", "Actif 30 j"], ["dormant", "Inactif > 30 j"]]} />
        <Select label="Inscription" value={filters.signup} onChange={setF("signup")} options={[["", "Toutes"], ["7", "7 derniers jours"], ["30", "30 derniers jours"]]} />
        <Select label="Origine" value={filters.origin} onChange={setF("origin")} options={[["", "Toutes"], ["friend_link", "Lien ami"], ["crew_link", "Lien crew"], ["dispo_link", "Lien dispo"], ["guest_web", "Invité web"], ["unknown", "Inconnue"]]} />
        <Select label="Photo" value={filters.avatar} onChange={setF("avatar")} options={[["", "–"], ["1", "Avec"], ["0", "Sans"]]} />
        <Select label="Notifs" value={filters.push} onChange={setF("push")} options={[["", "–"], ["1", "Activées"], ["0", "Aucune"]]} />
        {activeFilters > 0 && (
          <button onClick={() => setFilters(DEFAULT_FILTERS)} className="text-sm text-slate-400 hover:text-white underline underline-offset-2 pb-1.5">
            Réinitialiser
          </button>
        )}
      </section>

      <div className="flex items-center justify-between mb-2 text-sm text-slate-400">
        <span>
          {total == null ? "…" : `${fmt(rows.length)} affichés sur ${fmt(total)}`}
          {activeFilters > 0 && " (filtrés)"}
        </span>
        <span className="text-xs">Clique une ligne pour le détail · clique un en-tête pour trier</span>
      </div>
      {error && <div className="mb-3 text-sm text-red-400">{error}</div>}

      {/* Table */}
      <section className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-x-auto">
        <table className="w-full text-sm min-w-[1400px]">
          <thead className="sticky top-0 bg-slate-900 z-10">
            <tr className="text-left text-xs text-slate-500">
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  onClick={c.sort ? () => toggleSort(c.sort) : undefined}
                  className={`px-3 py-2.5 font-medium whitespace-nowrap ${c.sort ? "cursor-pointer hover:text-slate-200 select-none" : ""} ${c.num ? "text-right" : ""} ${sort.key === c.sort ? "text-sky-300" : ""}`}
                >
                  {c.label}
                  {sort.key === c.sort && <span className="ml-1">{sort.dir === "asc" ? "↑" : "↓"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <>
                <tr
                  key={u.id}
                  onClick={() => setExpanded((e) => (e === u.id ? null : u.id))}
                  className={`border-t border-slate-800/80 cursor-pointer hover:bg-slate-800/40 ${expanded === u.id ? "bg-slate-800/40" : ""}`}
                >
                  {COLUMNS.map((c) => (
                    <td key={c.key} className={`px-3 py-2 whitespace-nowrap text-slate-200 ${c.num ? "text-right tabular-nums" : ""}`}>
                      {cell(u, c)}
                    </td>
                  ))}
                </tr>
                {expanded === u.id && (
                  <tr key={`${u.id}-d`} className="bg-slate-950/60">
                    <td colSpan={COLUMNS.length} className="px-4 py-3 text-xs text-slate-300">
                      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-1">
                        <div><span className="text-slate-500">Id :</span> <code className="text-slate-400">{u.id}</code></div>
                        <div><span className="text-slate-500">Inscrit le :</span> {dateTime(u.createdAt)}</div>
                        <div><span className="text-slate-500">Dernière activité :</span> {dateTime(u.lastActiveAt)}</div>
                        <div><span className="text-slate-500">Origine :</span> {origin(u).label}{u.acquisitionAt ? ` (${dateTime(u.acquisitionAt)})` : ""}</div>
                        <div>
                          <span className="text-slate-500">Premier contact :</span>{" "}
                          {u.firstContact
                            ? `${u.firstContact.direction === "received" ? "demande reçue de" : "demande envoyée à"} ${u.firstContact.login} le ${dateTime(u.firstContact.at)} (${u.firstContact.status.toLowerCase()})`
                            : "aucune demande d'ami"}
                        </div>
                        <div><span className="text-slate-500">Invité web avant inscription :</span> {u.guestBefore ? "oui" : "non"}</div>
                        <div><span className="text-slate-500">Première dispo d'un autre :</span> {u.firstHost ? `de ${u.firstHost.login}, créée le ${dateTime(u.firstHost.at)}` : "aucune"}</div>
                        <div><span className="text-slate-500">Signalements reçus :</span> {u.reportsReceived} · <span className="text-slate-500">blocages reçus :</span> {u.blocksReceived}</div>
                        <div><span className="text-slate-500">Sessions :</span> {u.providers.map((p) => PROVIDER_LABEL[p] || p).join(", ") || "–"} · {u.devices.map((d) => DEVICE_LABEL[d] || d).join(", ") || "–"}</div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-slate-500">Aucun utilisateur ne correspond</td></tr>
            )}
          </tbody>
        </table>
        <div ref={sentinel} className="py-3 text-center text-xs text-slate-500">
          {loading ? "Chargement…" : hasMore ? "Fais défiler pour charger la suite" : rows.length > 0 ? "Fin de la liste" : ""}
        </div>
      </section>
    </div>
  );
}
